/**
 * HTML marker builder for globe.gl 3D globe.
 *
 * Provides a discriminated union of 20+ marker kinds and a
 * `buildMarkerElement` function that returns a styled HTMLElement
 * for each kind. Designed for use with globe.gl's htmlElementsData layer.
 */

import { isAnimationEnabled } from './globe-render-settings';

// ── Marker kind definitions ─────────────────────────────────────────

interface MarkerBase {
  lat: number;
  lng: number;
}

interface ConflictMarker extends MarkerBase {
  _kind: 'conflict';
  name: string;
  fatalities: number;
  type: string;
}

interface HotspotMarker extends MarkerBase {
  _kind: 'hotspot';
  label: string;
  severity: 1 | 2 | 3 | 4 | 5;
}

interface FlightMarker extends MarkerBase {
  _kind: 'flight';
  callsign: string;
  altitude: number;
  country: string;
  heading: number;
}

interface VesselMarker extends MarkerBase {
  _kind: 'vessel';
  name: string;
  mmsi: string;
  country: string;
  type: string;
}

interface EarthquakeMarker extends MarkerBase {
  _kind: 'earthquake';
  magnitude: number;
  depth: number;
  location: string;
}

interface SatelliteMarker extends MarkerBase {
  _kind: 'satellite';
  name: string;
  noradId: number;
  country: string;
  alt: number;
}

interface FireMarker extends MarkerBase {
  _kind: 'fire';
  brightness: number;
  confidence: number;
  frp: number;
}

interface NuclearMarker extends MarkerBase {
  _kind: 'nuclear';
  name: string;
  status: 'operational' | 'decommissioned' | 'under-construction';
}

interface MilbaseMarker extends MarkerBase {
  _kind: 'milbase';
  name: string;
  alliance: 'us-nato' | 'russia' | 'china' | 'india' | 'other';
}

interface DatacenterMarker extends MarkerBase {
  _kind: 'datacenter';
  name: string;
  operator: string;
}

interface WeatherMarker extends MarkerBase {
  _kind: 'weather';
  type: string;
  severity: 'minor' | 'moderate' | 'severe' | 'extreme';
}

interface RadiationMarker extends MarkerBase {
  _kind: 'radiation';
  level: number;
  unit: string;
  station: string;
}

interface ProtestMarker extends MarkerBase {
  _kind: 'protest';
  title: string;
  size: number;
}

interface CyberMarker extends MarkerBase {
  _kind: 'cyber';
  target: string;
  attackType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

interface EconomicMarker extends MarkerBase {
  _kind: 'economic';
  indicator: string;
  value: number;
  trend: 'up' | 'down' | 'stable';
}

interface ClusterMarker extends MarkerBase {
  _kind: 'cluster';
  count: number;
  kinds: string[];
}

interface GpsjamMarker extends MarkerBase {
  _kind: 'gpsjam';
  severity: 1 | 2 | 3 | 4 | 5;
  source: string;
}

interface DarkshipMarker extends MarkerBase {
  _kind: 'darkship';
  name: string;
  mmsi: string;
  lastSeen: string;
  country: string;
}

interface DisplacementMarker extends MarkerBase {
  _kind: 'displacement';
  population: number;
  origin: string;
  cause: string;
}

interface PortMarker extends MarkerBase {
  _kind: 'port';
  name: string;
  country: string;
  vessels: number;
}

interface NewsMarker extends MarkerBase {
  _kind: 'news';
  headline: string;
  source: string;
  category: string;
}

type GlobeMarker =
  | ConflictMarker
  | HotspotMarker
  | FlightMarker
  | VesselMarker
  | EarthquakeMarker
  | SatelliteMarker
  | FireMarker
  | NuclearMarker
  | MilbaseMarker
  | DatacenterMarker
  | WeatherMarker
  | RadiationMarker
  | ProtestMarker
  | CyberMarker
  | EconomicMarker
  | ClusterMarker
  | GpsjamMarker
  | DarkshipMarker
  | DisplacementMarker
  | PortMarker
  | NewsMarker;

// ── Color maps ──────────────────────────────────────────────────────

const HOTSPOT_COLORS: Record<number, string> = {
  5: '#ff2020',
  4: '#ff6600',
  3: '#ffaa00',
  2: '#ffdd00',
  1: '#88ff44',
};

const SATELLITE_COUNTRY_COLORS: Record<string, string> = {
  CN: '#ff2020',
  RU: '#ff8800',
  US: '#4488ff',
  EU: '#44cc44',
  KR: '#aa66ff',
  IN: '#ff66aa',
  TR: '#ff4466',
  OTHER: '#ccccff',
};

const MILBASE_COLORS: Record<string, string> = {
  'us-nato': '#4488ff',
  russia: '#ff4444',
  china: '#ff8844',
  india: '#ff8844',
  other: '#aaaaaa',
};

const WEATHER_SEVERITY_COLORS: Record<string, string> = {
  minor: '#88ccff',
  moderate: '#ffcc00',
  severe: '#ff8800',
  extreme: '#ff2020',
};

const CYBER_SEVERITY_COLORS: Record<string, string> = {
  low: '#88ff44',
  medium: '#ffcc00',
  high: '#ff8800',
  critical: '#ff2020',
};

// ── Style injection ─────────────────────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
@keyframes sentinel-pulse {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.3); opacity: 0.5; }
}
@keyframes sentinel-glow {
  0%, 100% { box-shadow: 0 0 4px currentColor; }
  50% { box-shadow: 0 0 10px currentColor; }
}
`;
  document.head.appendChild(style);
}

// ── Helper: create a base element with common styles ────────────────
//
// IMPORTANT: these build the VISUAL node only. globe.gl hands the
// returned root element directly to three's CSS2DObject, whose renderer
// rewrites `style.transform` on it EVERY frame — so the root must never
// carry a transform or a transform-animating CSS animation (they fight
// over the same property: headings get clobbered, pulsing markers lose
// their positioning and trail during camera rotation). All visuals live
// on inner children; `wrapForCSS2D` supplies the bare root + WM's
// 20x20px hit target.

/** Strip animations when the render-scale disables them (eco mode). */
function withAnimationGate(extra?: Partial<CSSStyleDeclaration>): Partial<CSSStyleDeclaration> | undefined {
  if (extra?.animation && !isAnimationEnabled()) {
    const { animation: _dropped, ...rest } = extra;
    return rest;
  }
  return extra;
}

function makeEl(
  size: number,
  color: string,
  extra?: Partial<CSSStyleDeclaration>,
): HTMLDivElement {
  const el = document.createElement('div');
  Object.assign(el.style, {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    color,
    ...withAnimationGate(extra),
  } satisfies Partial<CSSStyleDeclaration>);
  return el;
}

function makeEmojiEl(
  emoji: string,
  size: number,
  color: string,
  extra?: Partial<CSSStyleDeclaration>,
): HTMLDivElement {
  const el = document.createElement('div');
  Object.assign(el.style, {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${size - 2}px`,
    lineHeight: `${size}px`,
    textAlign: 'center',
    textShadow: `0 0 6px ${color}`,
    position: 'relative',
    color,
    ...withAnimationGate(extra),
  } satisfies Partial<CSSStyleDeclaration>);
  el.textContent = emoji;
  return el;
}

/**
 * Wrap a visual node for CSS2D rendering: a bare root (the renderer
 * owns its transform) holding WM's 20x20px centered hit target. The
 * tooltip moves to the root so the whole hit area carries it.
 */
function wrapForCSS2D(visual: HTMLElement): HTMLElement {
  const root = document.createElement('div');
  Object.assign(root.style, {
    pointerEvents: 'auto',
    cursor: 'pointer',
    userSelect: 'none',
  } satisfies Partial<CSSStyleDeclaration>);

  const hit = document.createElement('div');
  Object.assign(hit.style, {
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies Partial<CSSStyleDeclaration>);

  hit.appendChild(visual);
  root.appendChild(hit);
  if (visual.title) {
    root.title = visual.title;
    visual.removeAttribute('title');
  }
  return root;
}

// ── Marker builders by kind ─────────────────────────────────────────

function buildConflict(m: ConflictMarker): HTMLElement {
  const size = Math.min(16, Math.max(8, 6 + Math.log10(Math.max(1, m.fatalities)) * 3));
  const el = makeEl(size, '#ff2020', {
    animation: 'sentinel-pulse 2s ease-in-out infinite',
    boxShadow: '0 0 6px #ff2020',
  });
  el.title = `${m.name} — ${m.fatalities} fatalities`;
  return el;
}

function buildHotspot(m: HotspotMarker): HTMLElement {
  const color = HOTSPOT_COLORS[m.severity] ?? '#ffaa00';
  const size = 6 + m.severity * 2;
  const el = makeEl(size, color, {
    animation: 'sentinel-glow 2.5s ease-in-out infinite',
  });
  el.title = `${m.label} (severity ${m.severity})`;
  return el;
}

function buildFlight(m: FlightMarker): HTMLElement {
  const el = makeEmojiEl('\u2708', 12, '#88ccff', {
    transform: `rotate(${m.heading}deg)`,
  });
  el.title = `${m.callsign} — ${Math.round(m.altitude)}ft`;
  return el;
}

function buildVessel(m: VesselMarker): HTMLElement {
  const color = SATELLITE_COUNTRY_COLORS[m.country] ?? SATELLITE_COUNTRY_COLORS.OTHER;
  const el = makeEmojiEl('\u{1F6A2}', 10, color);
  el.title = `${m.name} (${m.mmsi})`;
  return el;
}

function buildEarthquake(m: EarthquakeMarker): HTMLElement {
  const color = m.magnitude >= 6 ? '#ff2020' : m.magnitude >= 4 ? '#ff8800' : '#ffdd00';
  const size = Math.min(16, Math.max(6, m.magnitude * 2));
  const el = makeEl(size, 'transparent', {
    border: `2px solid ${color}`,
    boxShadow: `0 0 6px ${color}`,
    animation: m.magnitude >= 5 ? 'sentinel-pulse 1.5s ease-in-out infinite' : 'none',
  });
  el.title = `M${m.magnitude.toFixed(1)} — ${m.location} (${m.depth}km)`;
  return el;
}

function buildSatellite(m: SatelliteMarker): HTMLElement {
  const color = SATELLITE_COUNTRY_COLORS[m.country] ?? SATELLITE_COUNTRY_COLORS.OTHER;
  const el = makeEmojiEl('\u{1F6F0}', 10, color);
  el.title = `${m.name} (${m.country})`;
  return el;
}

function buildFire(m: FireMarker): HTMLElement {
  const size = Math.min(14, Math.max(6, 6 + (m.frp / 100) * 4));
  // Brightness-temperature tiers (WM GlobeMap.ts:1184)
  const color = m.brightness > 400 ? '#ff2020' : m.brightness > 330 ? '#ff6600' : '#ffaa00';
  const el = makeEmojiEl('\u{1F525}', size, color);
  el.title = `Fire — brightness ${m.brightness.toFixed(0)}, FRP ${m.frp.toFixed(0)}`;
  return el;
}

function buildNuclear(m: NuclearMarker): HTMLElement {
  const color = m.status === 'operational' ? '#ff8800' : m.status === 'under-construction' ? '#ffcc00' : '#888888';
  const el = makeEmojiEl('\u2622', 10, color);
  el.title = `${m.name} — ${m.status}`;
  return el;
}

function buildMilbase(m: MilbaseMarker): HTMLElement {
  const color = MILBASE_COLORS[m.alliance] ?? MILBASE_COLORS.other;
  const el = makeEl(8, color, {
    borderRadius: '2px',
    animation: 'sentinel-glow 3s ease-in-out infinite',
  });
  el.title = `${m.name} (${m.alliance})`;
  return el;
}

function buildDatacenter(m: DatacenterMarker): HTMLElement {
  const el = makeEmojiEl('\u{1F5A5}', 10, '#44ccff');
  el.title = `${m.name} — ${m.operator}`;
  return el;
}

function buildWeather(m: WeatherMarker): HTMLElement {
  const color = WEATHER_SEVERITY_COLORS[m.severity] ?? '#88ccff';
  const el = makeEmojiEl('\u26C8', 12, color, {
    animation: m.severity === 'extreme' ? 'sentinel-pulse 2s ease-in-out infinite' : 'none',
  });
  el.title = `${m.type} — ${m.severity}`;
  return el;
}

function buildRadiation(m: RadiationMarker): HTMLElement {
  const color = m.level > 1.0 ? '#ff2020' : m.level > 0.3 ? '#ff8800' : '#88ff44';
  const el = makeEmojiEl('\u2622', 10, color, {
    animation: m.level > 1.0 ? 'sentinel-pulse 1.5s ease-in-out infinite' : 'none',
  });
  el.title = `${m.station} — ${m.level} ${m.unit}`;
  return el;
}

function buildProtest(m: ProtestMarker): HTMLElement {
  const size = Math.min(14, Math.max(8, 8 + Math.log10(Math.max(1, m.size)) * 2));
  const el = makeEmojiEl('\u270A', size, '#ffcc00');
  el.title = `${m.title} (~${m.size} people)`;
  return el;
}

function buildCyber(m: CyberMarker): HTMLElement {
  const color = CYBER_SEVERITY_COLORS[m.severity] ?? '#ffcc00';
  const el = makeEmojiEl('\u{1F4BB}', 10, color, {
    animation: m.severity === 'critical' ? 'sentinel-pulse 1.5s ease-in-out infinite' : 'none',
  });
  el.title = `${m.attackType} on ${m.target} — ${m.severity}`;
  return el;
}

function buildEconomic(m: EconomicMarker): HTMLElement {
  const emoji = m.trend === 'up' ? '\u{1F4C8}' : m.trend === 'down' ? '\u{1F4C9}' : '\u{1F4CA}';
  const color = m.trend === 'up' ? '#44cc44' : m.trend === 'down' ? '#ff4444' : '#ffcc00';
  const el = makeEmojiEl(emoji, 10, color);
  el.title = `${m.indicator}: ${m.value}`;
  return el;
}

function buildCluster(m: ClusterMarker): HTMLElement {
  const size = Math.min(16, Math.max(10, 10 + Math.log10(m.count) * 4));
  const el = makeEl(size, '#6644cc', {
    border: '2px solid #9966ff',
    fontSize: '9px',
    fontWeight: 'bold',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#ffffff',
    background: 'rgba(66, 33, 120, 0.85)',
  });
  el.textContent = m.count > 999 ? `${(m.count / 1000).toFixed(1)}k` : String(m.count);
  el.title = `Cluster: ${m.count} items (${m.kinds.join(', ')})`;
  return el;
}

function buildGpsjam(m: GpsjamMarker): HTMLElement {
  const colors: Record<number, string> = {
    1: '#88ff44',
    2: '#ffdd00',
    3: '#ffaa00',
    4: '#ff6600',
    5: '#ff2020',
  };
  const color = colors[m.severity] ?? '#ffaa00';
  const el = makeEmojiEl('\u{1F4E1}', 12, color, {
    animation: m.severity >= 4 ? 'sentinel-pulse 1.5s ease-in-out infinite' : 'none',
  });
  el.title = `GPS jamming — severity ${m.severity} (${m.source})`;
  return el;
}

function buildDarkship(m: DarkshipMarker): HTMLElement {
  const el = makeEmojiEl('\u{1F6A2}', 10, '#ff4444', {
    animation: 'sentinel-pulse 2s ease-in-out infinite',
  });
  // Add a warning ring
  const ring = document.createElement('div');
  Object.assign(ring.style, {
    position: 'absolute',
    top: '-4px',
    left: '-4px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '1px dashed #ff4444',
    animation: 'sentinel-pulse 2.5s ease-in-out infinite',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);
  el.appendChild(ring);
  el.title = `Dark ship: ${m.name} (${m.mmsi}) — last seen ${m.lastSeen}`;
  return el;
}

function buildDisplacement(m: DisplacementMarker): HTMLElement {
  const size = Math.min(14, Math.max(8, 8 + Math.log10(Math.max(1, m.population)) * 1.5));
  const el = makeEmojiEl('\u{1F6B6}', size, '#ff8866');
  el.title = `${m.population.toLocaleString()} displaced from ${m.origin} — ${m.cause}`;
  return el;
}

function buildPort(m: PortMarker): HTMLElement {
  const el = makeEmojiEl('\u2693', 10, '#44aaff');
  el.title = `${m.name} (${m.country}) — ${m.vessels} vessels`;
  return el;
}

function buildNews(m: NewsMarker): HTMLElement {
  const el = makeEmojiEl('\u{1F4F0}', 10, '#ddddff');
  el.title = `${m.headline} — ${m.source}`;
  return el;
}

// ── Public API ──────────────────────────────────────────────────────

function buildMarkerElement(marker: GlobeMarker): HTMLElement {
  injectStyles();
  return wrapForCSS2D(buildVisual(marker));
}

/** Build the styled inner node for a marker kind. */
function buildVisual(marker: GlobeMarker): HTMLElement {
  switch (marker._kind) {
    case 'conflict':      return buildConflict(marker);
    case 'hotspot':       return buildHotspot(marker);
    case 'flight':        return buildFlight(marker);
    case 'vessel':        return buildVessel(marker);
    case 'earthquake':    return buildEarthquake(marker);
    case 'satellite':     return buildSatellite(marker);
    case 'fire':          return buildFire(marker);
    case 'nuclear':       return buildNuclear(marker);
    case 'milbase':       return buildMilbase(marker);
    case 'datacenter':    return buildDatacenter(marker);
    case 'weather':       return buildWeather(marker);
    case 'radiation':     return buildRadiation(marker);
    case 'protest':       return buildProtest(marker);
    case 'cyber':         return buildCyber(marker);
    case 'economic':      return buildEconomic(marker);
    case 'cluster':       return buildCluster(marker);
    case 'gpsjam':        return buildGpsjam(marker);
    case 'darkship':      return buildDarkship(marker);
    case 'displacement':  return buildDisplacement(marker);
    case 'port':          return buildPort(marker);
    case 'news':          return buildNews(marker);
  }
}

function getMarkerAltitude(marker: GlobeMarker): number {
  switch (marker._kind) {
    case 'satellite':  return marker.alt / 6371;
    case 'flight':
    case 'vessel':
    case 'cluster':    return 0.012;
    case 'hotspot':    return 0.005;
    default:           return 0.003;
  }
}

export type { GlobeMarker };
export { buildMarkerElement, getMarkerAltitude };
